/**
 * LabelsProvider — wraps an Ads-Manager page subtree and exposes the
 * labels list (+ pre-grouped lookup map) via LabelsContext. Pages
 * fetch the labels with `useLabels()` and pass the array in here;
 * deep consumers (StatsTable's MetricCells render the Label column)
 * read via `useLabelsContext` / `useLabelsForEntity`.
 */

import { useMemo, type ReactNode } from "react";
import {
  LabelsContext,
  type LabelsByEntity,
  type LabelsContextValue,
} from "@/lib/labels-context";
import type { Label } from "@/lib/types";

export function LabelsProvider({
  labels,
  children,
}: {
  labels: Label[];
  children: ReactNode;
}) {
  const value = useMemo<LabelsContextValue>(() => {
    const byCampaign = new Map<number, Label[]>();
    const byAdGroup = new Map<number, Label[]>();
    for (const lab of labels) {
      for (const ref of lab.applied_to) {
        const map = ref.entity_type === "campaign" ? byCampaign : byAdGroup;
        const list = map.get(ref.entity_id);
        if (list) list.push(lab);
        else map.set(ref.entity_id, [lab]);
      }
    }
    const labelsByEntity: LabelsByEntity = {
      campaign: byCampaign,
      ad_group: byAdGroup,
    };
    return { labels, labelsByEntity };
  }, [labels]);

  return (
    <LabelsContext.Provider value={value}>{children}</LabelsContext.Provider>
  );
}
